import { Fragment, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BackButton, SearchFilterBar, DataTable, formatMonthDayTime24,
  type FilterDef, type ColumnDef, type SortState,
} from '@shared-ui/components';
import { TopicSelector } from '@shared-ui/components/TopicSelector';
import { QuestionSearchFilter, type QuestionSearchOption } from '@shared-ui/question-picker';
import { type UserSearchOption } from '@shared-ui/user-picker';
import { ScopedUserSearchFilter } from '../components/ScopedUserSearchFilter';
import { useModules } from '@shared-ui/reference-data';
import { LatexText } from '@shared-ui/latex';
import { SUBJECT_OPTIONS, SUBJECT_NAMES } from '@padcanvas/shared';
import { fetchWithAdmin } from '../api/fetchHelper';
import './LearningMoments.css';

interface ObservedEncounterRow {
  momentId: string;
  momentName: string;
  submissionId: string;
  questionId: string;
  moduleId: string;
  topic?: string;
  subject?: string;
  userId: string;
  studentName?: string;
  studentEmail?: string;
  wasPositive: boolean;
  detectedAt: string;
  context: string;
  questionText?: string;
}

interface ObservedEncountersData {
  encounters: ObservedEncounterRow[];
}

interface MomentOption {
  id: string;
  name: string;
}

/**
 * Every hit/miss across every learning moment, system-wide.
 *
 * The generic sibling of the teacher app's per-moment drill-down (still
 * `MomentEncounters` there, opened one moment at a time from a student's own
 * view). This one is the admin explorer: browse by student, moment type,
 * subject, topic or question instead of only ever seeing one moment's own
 * history.
 *
 * `?type=<momentId>` preselects the Learning moment type filter — the
 * Learning Moments list still lands here when a row (or its Hits/Misses
 * button) is clicked, scoped to that moment, but the filter is an ordinary
 * filter from there, not the whole page's identity, so it can be cleared or
 * changed without leaving the page.
 *
 * Server-driven: every filter (student, type, subject, topic, question,
 * result) is a query param on `/system/learning-moments/observed` — the
 * server does the filtering and only the matching rows come back. Nothing
 * here holds a full unfiltered table to filter locally.
 */
export function LearningMomentsObserved() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [values, setValues] = useState<Record<string, string>>(() => {
    const type = searchParams.get('type');
    return type ? { momentId: type } : ({} as Record<string, string>);
  });
  const [sort, setSort] = useState<SortState>({ sortBy: 'when', sortOrder: 'desc' });

  // Custom controls (student, topic, question) keep their value in the same
  // `values` map as the ordinary filters — Clear replaces the whole map, so
  // keeping them here is what makes Clear drop them too.
  // UserSearchFilter is keyed on email (studentEmail, for the control's own
  // chip/value binding); studentId is captured separately via onPick, since
  // the backend filter needs the id, not the email.
  const studentEmail = values.studentEmail || '';
  const studentId = values.studentId || '';
  const moduleId = values.moduleId || '';
  const questionId = values.questionId || '';

  const { modules, getModulesForSubject } = useModules();
  const topicModules = values.subject ? getModulesForSubject(values.subject) : modules;

  // Canonical moment-type list for the filter — stable regardless of the
  // current result set, unlike deriving options from loaded rows (which
  // would shrink the dropdown as other filters narrow the table).
  const { data: momentsData } = useQuery({
    queryKey: ['learning-moments-list'],
    queryFn: () => fetchWithAdmin<MomentOption[]>('/console/learning-moments'),
  });
  const moments = useMemo(() => momentsData ?? [], [momentsData]);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (studentId) p.set('userId', studentId);
    if (values.momentId) p.set('momentId', values.momentId);
    if (values.subject) p.set('subject', values.subject);
    if (moduleId) p.set('moduleId', moduleId);
    if (questionId) p.set('questionId', questionId);
    if (values.result) p.set('result', values.result);
    return p.toString();
  }, [studentId, moduleId, questionId, values.momentId, values.subject, values.result]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['learning-moments-observed', queryString],
    queryFn: () => fetchWithAdmin<ObservedEncountersData>(
      `/system/learning-moments/observed${queryString ? `?${queryString}` : ''}`,
    ),
  });

  const rows = useMemo(() => data?.encounters ?? [], [data]);
  const anyFilterActive = !!(studentId || values.momentId || values.subject || moduleId || questionId || values.result);

  const filters: FilterDef[] = useMemo(() => [
    {
      key: 'momentId',
      placeholder: 'All types',
      options: [...moments]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(m => ({ value: m.id, label: m.name })),
    } as FilterDef,
    {
      key: 'subject',
      placeholder: 'All subjects',
      options: SUBJECT_OPTIONS.map(s => ({ value: s, label: SUBJECT_NAMES[s] ?? s })),
    } as FilterDef,
    {
      key: 'result',
      placeholder: 'All results',
      options: [
        { value: 'hits', label: 'Hits' },
        { value: 'misses', label: 'Misses' },
      ],
    } as FilterDef,
  ], [moments]);

  // Distinct questions in the current (already server-filtered) rows, most
  // frequent first — offered as suggestions so the common pick needs no
  // search. Typing still searches the whole bank.
  const questionSuggestions: QuestionSearchOption[] = useMemo(() => {
    const counts = new Map<string, { text?: string; n: number }>();
    for (const e of rows) {
      if (!e.questionId) continue;
      const prev = counts.get(e.questionId);
      if (prev) prev.n++;
      else counts.set(e.questionId, { text: e.questionText, n: 1 });
    }
    return [...counts.entries()]
      .sort((a, b) => b[1].n - a[1].n)
      .map(([id, { text, n }]) => ({
        questionId: id,
        questionText: text,
        hint: `${n} row${n === 1 ? '' : 's'}`,
      }));
  }, [rows]);

  // Every column sorts except Context — free text with no natural order, and
  // the reason a row is here, not something to scan sorted.
  const columns: ColumnDef<ObservedEncounterRow>[] = useMemo(() => [
    {
      key: 'student',
      header: 'Student',
      sortable: true,
      sortValue: e => (e.studentName || e.studentEmail || e.userId).toLowerCase(),
      render: e => (
        <>
          <div className="lm-name">{e.studentName || e.studentEmail || e.userId}</div>
          {e.studentName && e.studentEmail && <div className="lm-desc-preview">{e.studentEmail}</div>}
        </>
      ),
    },
    {
      key: 'moment',
      header: 'Learning moment',
      sortable: true,
      sortValue: e => e.momentName.toLowerCase(),
      render: e => (
        <div className="lm-moment-cell">
          <span className="lm-name">{e.momentName}</span>
          <span className={`lm-status ${e.wasPositive ? 'lm-active' : 'lm-inactive'}`}>
            {e.wasPositive ? 'Hit' : 'Miss'}
          </span>
        </div>
      ),
    },
    {
      key: 'topic',
      header: 'Topic',
      sortable: true,
      sortValue: e => `${e.subject ?? ''} ${e.topic ?? ''}`.toLowerCase(),
      className: 'lm-targeting',
      render: e => {
        const subjectLabel = e.subject ? (SUBJECT_NAMES[e.subject] ?? e.subject) : undefined;
        if (subjectLabel && e.topic) return `${subjectLabel} · ${e.topic}`;
        return subjectLabel || e.topic || '—';
      },
    },
    {
      key: 'question',
      header: 'Question',
      sortable: true,
      sortValue: e => (e.questionText || '').toLowerCase(),
      className: 'lm-question',
      render: e => (
        <span title={e.questionText}>
          {e.questionText ? <LatexText text={e.questionText} truncate={90} /> : '—'}
        </span>
      ),
    },
    {
      key: 'when',
      header: 'When',
      sortable: true,
      sortValue: e => e.detectedAt,
      className: 'lm-date',
      render: e => formatMonthDayTime24(e.detectedAt),
    },
    {
      key: 'context',
      header: 'Context',
      className: 'lm-desc-preview',
      render: e => <span title={e.context}>{e.context}</span>,
    },
  ], []);

  return (
    <div className="lm-page">
      <BackButton label="list" to="/learning-moments" />
      <h1>Learning Moments Observed</h1>

      <SearchFilterBar
        filters={filters}
        values={values}
        onChange={setValues}
        hideSearch
        hideFetch
        leading={(
          <ScopedUserSearchFilter
            value={studentEmail}
            onChange={email => setValues(v => ({ ...v, studentEmail: email }))}
            onPick={(o: UserSearchOption | null) => setValues(v => ({ ...v, studentId: o?.userId ?? '' }))}
            placeholder="Student name or email…"
          />
        )}
        renderAfter={{
          // Both belong right after Subject — topic is scoped by it, and
          // question-by-text reads naturally beside topic-by-tree — so they
          // share Subject's slot rather than splitting across two anchors.
          subject: (
            <Fragment>
              <TopicSelector
                modules={topicModules}
                value={moduleId}
                onChange={id => setValues(v => ({ ...v, moduleId: id }))}
                placeholder="All topics"
              />
              <QuestionSearchFilter
                  includeOwned
                suggestions={questionSuggestions}
                suggestionsLabel="Questions in these encounters"
                value={questionId}
                onChange={id => setValues(v => ({ ...v, questionId: id }))}
                placeholder="Filter by question…"
                subject={values.subject || undefined}
              />
            </Fragment>
          ),
        }}
      />
      <div className="lm-count-row">
        <span className="lm-count">{rows.length} shown</span>
      </div>

      {isLoading ? (
        <p>Loading...</p>
      ) : error ? (
        <div className="lm-error">{error instanceof Error ? error.message : 'Failed to load encounters'}</div>
      ) : !rows.length ? (
        <p className="lm-empty">
          {anyFilterActive ? 'No encounters match the current filters.' : 'No encounters recorded yet.'}
        </p>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          rowKey={e => `${e.submissionId}-${e.questionId}-${e.momentId}`}
          onRowClick={(row) => navigate(`/submissions/${row.submissionId}`, {
            state: { backTo: '/learning-moments/encounters', backLabel: 'Learning Moments Observed' },
          })}
          sort={sort}
          onSortChange={setSort}
          bare
        />
      )}
    </div>
  );
}
